import React, { useEffect, useLayoutEffect, useState } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import pluralize from "pluralize";
import { Formik, yupToFormErrors } from "formik";
import * as Yup from "yup";
import { SM_PIXEL_MIN, XS_PIXEL_MAX } from "constants/breakpoints";
import InputTextCounter from "components/shared/InputTextCounter";
import Select from "components/shared/Select";
import IconButton from "components/shared/IconButton";
import DisclaimerText from "components/ai_tools/shared/AIForm/DisclaimerText";
import { useAccessTier } from "contexts/AccessTierContext";
import InfoBanner from "./InfoBanner";

const sessionStorageKey = "askLitchartsAi";

const validationSchema = Yup.object().shape({
  question: Yup.string()
    .required("25 characters required")
    .min(25, "25 characters required")
    .max(300, "Over max character limit"),
  title: Yup.string().required("Please select your title")
});

const AIForm = (props) => {
  const {
    loading,
    onSubmit,
    options = [],
    questionMinimum,
    quotaRemaining,
    infiniteAnswers,
    resetDate,
    signupPath,
    values,
    canAskQuestions
  } = props;

  const [isLessThanSmallBreakpoint, setIsLessThanSmallBreakpoint] =
    useState(true);
  const [collapsed, setCollapsed] = useState(true);

  const { isSignedOut, isFree } = useAccessTier();

  const disableFields =
    !isSignedOut && !isFree && quotaRemaining === 0 && !infiniteAnswers;
  const resizeHandler = () => {
    if (window.matchMedia(`(min-width: ${SM_PIXEL_MIN}px)`).matches) {
      setIsLessThanSmallBreakpoint(false);
    } else if (window.matchMedia(`(max-width: ${XS_PIXEL_MAX}px)`).matches) {
      setIsLessThanSmallBreakpoint(true);
    }
  };

  useLayoutEffect(() => {
    resizeHandler();
    window.addEventListener("resize", resizeHandler);
    return () => {
      window.removeEventListener("resize", resizeHandler);
    };
  }, []);

  useLayoutEffect(() => {
    setCollapsed(isLessThanSmallBreakpoint);
  }, [isLessThanSmallBreakpoint]);

  useEffect(() => {
    // Prevent the body from scrolling underneath the form field when on small screen
    // and expanded
    if (!collapsed && isLessThanSmallBreakpoint) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [collapsed]);

  const buttonRemainingText =
    canAskQuestions && !infiniteAnswers ? ` (${quotaRemaining} left)` : "";
  let buttonCTA = "Ask LitCharts AI";
  const freeTierButtonText = "Ask LitCharts AI for Free";
  if (quotaRemaining > 0 && !collapsed && isLessThanSmallBreakpoint) {
    buttonCTA = "Submit";
  }

  const buttonText = buttonCTA + buttonRemainingText;
  const resetText = `${pluralize(
    "answers",
    quotaRemaining,
    true
  )} left (resets on ${resetDate})`;
  const showDisclaimerText =
    !collapsed || (collapsed && quotaRemaining === 0 && !infiniteAnswers);

  const unpackValueFromStorage = (key) => {
    const storageKey = `${sessionStorageKey}:${key}`;
    const storedValue = sessionStorage.getItem(storageKey) || "";

    sessionStorage.removeItem(storageKey);

    return storedValue;
  };

  const initialValues = {
    question: !disableFields
      ? unpackValueFromStorage("question") || values.question
      : "",
    title: !disableFields ? unpackValueFromStorage("title") || values.title : ""
  };

  const storeFieldForReload = (key, value) => {
    if (!canAskQuestions && !disableFields) {
      const storageKey = `${sessionStorageKey}:${key}`;
      sessionStorage.setItem(storageKey, value);
    }
  };

  const handleSubmit = (formValues) => {
    // Can't ask any more questions, redirect to sales page
    if (!canAskQuestions && !disableFields) {
      window.location.href = signupPath;
      return;
    }

    // We're in a collapsed state (smaller breakpoints)
    // Expand the form
    if (collapsed) {
      setCollapsed(false);
      return;
    }

    // pass the values up to the parent to handle submission
    onSubmit(formValues);

    // If we're in smaller breakpoints, collapse the form
    if (isLessThanSmallBreakpoint) {
      setCollapsed(true);
    }
  };

  const validateForm = async (formValues) => {
    if (!canAskQuestions && isFree) return;
    let yupFormErrors = {};

    if (collapsed && isLessThanSmallBreakpoint) {
      return yupFormErrors;
    }

    if (!canAskQuestions) {
      return yupFormErrors;
    }

    try {
      await validationSchema.validate(formValues, { abortEarly: false });
    } catch (e) {
      yupFormErrors = yupToFormErrors(e);
    }

    return yupFormErrors;
  };

  const handleClose = () => {
    setCollapsed(true);
  };

  const amplitudeSignupAttr = () => {
    if (isSignedOut) return "standaloneFreeTierSubmitLargeSmall";
    let attr;
    if (!canAskQuestions && !disableFields) {
      attr = "aiSignupForm"; // we're free tier and signing up
    } else {
      attr = "aiQuestionAsked"; // we're subbed and asking a question
    }
    attr += isLessThanSmallBreakpoint ? "Small" : "Large";
    if (collapsed && isLessThanSmallBreakpoint) {
      attr += "Collapsed";
    }
    return attr;
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnMount={false}
      validate={validateForm}
      onSubmit={handleSubmit}
    >
      {(formik) => (
        <form
          onSubmit={formik.handleSubmit}
          className={classnames("ai-form--container", { collapsed })}
        >
          <div className="ai-form--header">
            <h1 className="title-5 tw-m-0">Ask LitCharts AI</h1>
            <div className="subtitle-1">
              The only tool that combines the world&apos;s best literary
              analysis with the power of AI.
            </div>
            <IconButton
              iconClass="lc-icon-close lc-icon-xs"
              btnClass="btn-lg btn-borderless-alt"
              onClick={handleClose}
            />
          </div>
          <div className="ai-form--content">
            <div className="ai-form--body">
              {!infiniteAnswers && (
                <InfoBanner
                  quotaRemaining={quotaRemaining}
                  resetDate={resetDate}
                  signupPath={signupPath}
                />
              )}
              <div className="ai-form--fields">
                <Select
                  errorMessage={formik.errors.title}
                  id="title"
                  isDisabled={loading || disableFields}
                  isSearchable
                  labelText="Title"
                  name="title"
                  options={options}
                  placeholder={
                    !disableFields ? "e.g. Hamlet (Shakespeare)" : ""
                  }
                  onChange={(e) => {
                    const fieldKey = "title";
                    storeFieldForReload(fieldKey, e.value);
                    formik.setFieldValue(fieldKey, e.value);
                  }}
                  value={formik.values.title}
                />
                <InputTextCounter
                  disabled={loading || disableFields}
                  hideSubtext={disableFields}
                  errorMessage={formik.errors.question}
                  labelText="Question"
                  key={`collapsed-${collapsed}`}
                  name="question"
                  min={questionMinimum}
                  max={300}
                  onChange={(e) => {
                    const fieldKey = "question";
                    formik.setFieldError(fieldKey, null);
                    storeFieldForReload(fieldKey, e.target.value);
                    formik.setFieldValue(fieldKey, e.target.value);
                  }}
                  placeholder={
                    !disableFields
                      ? "e.g. In Act 3, scene 3, why does Hamlet decide not to kill Claudius while Claudius is praying?"
                      : ""
                  }
                  value={formik.values.question}
                />
              </div>
            </div>
            <div className="ai-form--footer">
              {isSignedOut ? (
                <button
                  data-toggle="modal"
                  data-target="#ask-ai-free-tier .modal"
                  data-testid="ai-form-submit"
                  data-amplitude={amplitudeSignupAttr()}
                  type="button"
                  className={`ai-form--submit btn btn-a-plus button-1 ${
                    isLessThanSmallBreakpoint ? "btn-lg" : "btn-block"
                  }`}
                >
                  {freeTierButtonText}
                </button>
              ) : (
                <button
                  className={`ai-form--submit btn btn-a-plus button-1 ${
                    isLessThanSmallBreakpoint ? "btn-lg" : "btn-block"
                  }`}
                  data-testid="ai-form-submit"
                  data-amplitude={amplitudeSignupAttr()}
                  disabled={disableFields || loading}
                  type="submit"
                >
                  {!canAskQuestions ? (
                    <i
                      className="lc-icon lc-icon-lock-filled lc-icon-sm"
                      data-testid="lock-icon"
                    />
                  ) : null}

                  {loading ? "Answering..." : buttonText}
                </button>
              )}

              {canAskQuestions && showDisclaimerText && (
                <div className="subtitle-1 tw-flex tw-pt-2 tw-text-gray-tint-40">
                  {collapsed && quotaRemaining === 0 && resetText}
                  {!collapsed && (
                    <DisclaimerText className="tw-self-center tw-flex-grow" />
                  )}
                </div>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

AIForm.propTypes = {
  canAskQuestions: PropTypes.bool.isRequired,
  infiniteAnswers: PropTypes.bool,
  loading: PropTypes.bool,
  onSubmit: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      value: PropTypes.string
    })
  ),
  questionMinimum: PropTypes.number,
  quotaRemaining: PropTypes.number.isRequired,
  resetDate: PropTypes.string,
  signupPath: PropTypes.string,
  values: PropTypes.shape({
    question: PropTypes.string,
    title: PropTypes.string
  })
};

AIForm.defaultProps = {
  infiniteAnswers: false,
  loading: false,
  onSubmit: () => {},
  options: [],
  questionMinimum: 25,
  resetDate: "",
  signupPath: "/sign-up?focus=teacher",
  values: {}
};

export default AIForm;
